"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { DateTime } from "luxon";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { expandBusyRangesToDays, isRoomTooSmall } from "@/lib/stay/rooms";
import {
  getRoomIdentityColour,
  roomBusyModifierKey,
  HOUSE_WIDE_BUSY,
} from "@/lib/stay/roomIdentityColours";

const HOUSE_WIDE_BUSY_MODIFIER_KEY = "houseWideBusy";
import { useToast } from "@/hooks/use-toast";
import { StayPriceBreakdownCard } from "./StayPriceBreakdownCard";
import type { DateRange } from "react-day-picker";
import {
  averageSeasonalNightlyRate,
  coerceSeasonalRates,
  windowTouchesSeason,
} from "@/lib/payments/staySeasonalRates";
import {
  DEFAULT_TIMEZONE,
  buildSlotOptions,
  toBusyRanges,
  computeIsBusy,
  fetchAvailability,
  launchPayfastCheckout,
  saveBookingLead,
  submitReserveSlot,
} from "@/lib/bookings/bookingClient";
import type { AvailabilityResponse, SlotOption } from "@/lib/bookings/bookingClient";
import { useFirebase } from "@/firebase/provider";
import { incrementBookingRequest } from "@/lib/firebase/services/profilesService";

const SLOT_DURATION_MINUTES = 60;

type BookingMode = "instant" | "manual" | "request";
type ActiveMode = "instant" | "request";

type PaymentConfig = {
  enabled: boolean;
  provider: "payfast" | null;
  amountZar: number | null;
  currency?: "ZAR";
};

interface BookingFormProps {
  profileOwnerId: string;
  profileOwnerName: string;
  variant?: "scheduling" | "stay";
  bookingMode?: BookingMode;
  ctaText?: string;
  serviceContext?: string[];
  availabilityOverride?: AvailabilityResponse;
  autoSelectFirstAvailableSlot?: boolean;
  paymentConfig?: PaymentConfig;
  qaAutoConfirmAfterSubmit?: boolean;
  initialCheckIn?: string;
  initialCheckOut?: string;
  initialGuests?: number;
  minimumNights?: number;
  maxGuests?: number;
  initialMessage?: string;
  nightlyRateZar?: number;
  originalNightlyRateZar?: number;
  depositPercent?: number;
  cleaningFeeZar?: number;
  /** G6: the host's seasonal rates (property level; rooms keep their own later). */
  seasonalRates?: unknown;
}

const stayBookingSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().trim().min(1, "Email is required.").email("Please enter a valid email address."),
  whatsappNumber: z.string().optional(),
  note: z.string().optional(),
});

const scheduleSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().trim().min(1, "Email is required.").email("Please enter a valid email address."),
  whatsappNumber: z.string().optional(),
  quantity: z.string().optional(),
  preferredDate: z.string().optional(),
  preferredTime: z.string().optional(),
  message: z.string().optional(),
});

type StayBookingValues = z.infer<typeof stayBookingSchema>;
type ScheduleValues = z.infer<typeof scheduleSchema>;

type BusyInterval = {
  start: Date;
  end: Date;
};

type RoomAvailability = {
  id: string;
  roomName: string;
  sleeps: number | null;
  pricePerNight: number | null;
  cleaningFee: number | null;
  /** G6: the seasons that govern this room's rate — own, or the property's inherited. */
  seasonalRates?: unknown;
  busyRanges: Array<{ start: string; end: string }>;
  availableForWindow: boolean | null;
  photoUrls?: string[];
};

type CompletedStayBooking = {
  bookingId?: string;
  accessToken?: string;
  status: "pending" | "confirmed";
};

type CompletedSchedulingBooking =
  | {
      kind: "instant";
      state: "instant" | "approval" | "cancelled";
      bookingId: string;
      accessToken: string | null;
    }
  | {
      kind: "request";
      bookingId: string;
      slotLabel: string;
      accessToken: string | null;
      status: "pending" | "confirmed";
      paymentEnabled: boolean;
      paymentVerified: boolean;
    };

type BookingStatusPayload = {
  success?: boolean;
  status?: string;
  payment?: {
    enabled?: boolean;
    provider?: string | null;
    status?: string;
    verified?: boolean;
  };
};

const toDateAtMidnight = (value: Date) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (value: Date, days: number) => {
  const d = new Date(value);
  d.setDate(d.getDate() + days);
  return d;
};

const toIsoDay = (value: Date) => {
  const d = toDateAtMidnight(value);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const datesOverlap = (startA: Date, endA: Date, startB: Date, endB: Date) =>
  startA < endB && endA > startB;

const parseIsoDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed;
};

export function BookingForm({
  profileOwnerId,
  profileOwnerName,
  variant = "scheduling",
  bookingMode = "request",
  ctaText,
  serviceContext = [],
  availabilityOverride,
  autoSelectFirstAvailableSlot = false,
  paymentConfig,
  qaAutoConfirmAfterSubmit = false,
  initialCheckIn,
  initialCheckOut,
  initialGuests,
  minimumNights = 1,
  maxGuests,
  initialMessage,
  nightlyRateZar,
  originalNightlyRateZar,
  depositPercent,
  cleaningFeeZar,
  seasonalRates,
}: BookingFormProps) {
  const { toast } = useToast();
  const router = useRouter();
  const { auth } = useFirebase();
  const isStay = variant === "stay";
  const ownerSupportsInstant = bookingMode === "instant";
  const resolvedMode: ActiveMode = bookingMode === "instant" ? "instant" : "request";

  const [availability, setAvailability] = useState<AvailabilityResponse | null>(availabilityOverride ?? null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(!availabilityOverride);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentCheckoutLoading, setPaymentCheckoutLoading] = useState(false);
  const successCardRef = useRef<HTMLDivElement | null>(null);

  const [mode, setMode] = useState<ActiveMode>(ownerSupportsInstant ? "instant" : "request");
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [completedBooking, setCompletedBooking] = useState<CompletedSchedulingBooking | null>(null);
  const [guestActionLoading, setGuestActionLoading] = useState<"confirm" | "cancel" | null>(null);
  const [guestActionMessage, setGuestActionMessage] = useState<string | null>(null);

  const [range, setRange] = useState<DateRange | undefined>(() => {
    const from = parseIsoDate(initialCheckIn);
    const to = parseIsoDate(initialCheckOut);
    return from && to && from < to ? { from, to } : undefined;
  });
  const [guests, setGuests] = useState<number>(
    Number.isFinite(initialGuests) && (initialGuests as number) > 0 ? Number(initialGuests) : 2,
  );
  const [busyIntervals, setBusyIntervals] = useState<BusyInterval[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<Set<string>>(new Set());
  const [stayCompleted, setStayCompleted] = useState<CompletedStayBooking | null>(null);

  // Multi-room properties: the guest books a specific room. Rooms are fetched
  // from the public availability API; picking one routes the booking (and its
  // payment) to the room's own calendar, so the room's OTA feeds block too.
  const [rooms, setRooms] = useState<RoomAvailability[] | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [soldOutDays, setSoldOutDays] = useState<Set<string>>(new Set());
  // Whole-house busy nights (whole-house bookings / cascade hosts), from the
  // same public availability payload — what the legend calls "House-wide busy".
  const [houseBusyDays, setHouseBusyDays] = useState<Set<string>>(new Set());
  const bookingProfileId = selectedRoomId || profileOwnerId;
  const selectedRoom = rooms?.find((room) => room.id === selectedRoomId) || null;
  const selectedRoomAvailable = selectedRoom ? selectedRoom.availableForWindow !== false : true;

  const timezone = availability?.timezone || DEFAULT_TIMEZONE;
  const requiresSecurePayment = Boolean(paymentConfig?.enabled && paymentConfig?.provider === "payfast");
  const displayDepositPercent = Math.min(100, Math.max(5, Math.round(depositPercent || 10)));

  const stayForm = useForm<StayBookingValues>({
    resolver: zodResolver(stayBookingSchema),
    disabled: isSubmitting || paymentCheckoutLoading,
    defaultValues: {
      name: "",
      email: "",
      whatsappNumber: "",
      note: initialMessage || "",
    },
  });

  const scheduleForm = useForm<ScheduleValues>({
    resolver: zodResolver(scheduleSchema),
    disabled: isSubmitting || paymentCheckoutLoading || completedBooking !== null,
    defaultValues: {
      name: "",
      email: "",
      whatsappNumber: "",
      preferredDate: "",
      preferredTime: "",
      message: "",
    },
  });

  useEffect(() => {
    if (availabilityOverride) {
      setAvailability(availabilityOverride);
      setAvailabilityError(null);
      setAvailabilityLoading(false);
      return;
    }

    const controller = new AbortController();
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    fetchAvailability(profileOwnerId, controller.signal)
      .then((data) => {
        setAvailability(data);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }
        setAvailabilityError(error.message || "Failed to load availability");
      })
      .finally(() => {
        setAvailabilityLoading(false);
      });

    return () => controller.abort();
  }, [profileOwnerId, availabilityOverride]);

  // Room list for multi-room properties. Fetched once (90-day busy horizon);
  // per-window availability is computed client-side from busyRanges. A
  // property without rooms returns an empty list and the form behaves exactly
  // as before — single-calendar booking.
  useEffect(() => {
    if (!isStay || !profileOwnerId || availabilityOverride) return;
    const controller = new AbortController();
    fetch(`/api/stays/rooms?profileId=${encodeURIComponent(profileOwnerId)}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : { rooms: [] }))
      .then((data) => {
        const list = Array.isArray(data.rooms) ? (data.rooms as RoomAvailability[]) : [];
        setRooms(list.length > 0 ? list : null);
        setSoldOutDays(new Set(Array.isArray(data.soldOutDays) ? (data.soldOutDays as string[]) : []));
        if (Array.isArray(data.houseBusyRanges)) {
          setHouseBusyDays(expandBusyRangesToDays(data.houseBusyRanges as Array<{ start: string; end: string }>));
        }
        if (list.length > 0 && pendingRoomSelectRef.current) {
          // A "Book this room" click landed before the list arrived.
          if (list.some((room) => room.id === pendingRoomSelectRef.current)) {
            setSelectedRoomId(pendingRoomSelectRef.current);
            pendingRoomSelectRef.current = null;
          }
        }
      })
      .catch(() => {
        // Availability API is an enhancement; the booking itself does not
        // depend on it. Silently fall back to single-calendar behaviour.
      });
    return () => controller.abort();
  }, [isStay, profileOwnerId, availabilityOverride]);

  // Availability is per window: when the dates change, no room selection can
  // be trusted to still be free.
  const [rangeFromMs, rangeToMs] = [range?.from?.getTime(), range?.to?.getTime()];
  useEffect(() => {
    setSelectedRoomId(null);
  }, [rangeFromMs, rangeToMs]);

  // Per-room busy nights, expanded from the room's own busy ranges
  // ([start, end) checkout-day exclusive). This — not the property document's
  // own bookings — is what a multi-room calendar shows.
  const roomBusyDays = useMemo(() => {
    if (!rooms) return null;
    const map: Record<string, Set<string>> = {};
    for (const room of rooms) {
      map[room.id] = expandBusyRangesToDays(room.busyRanges || []);
    }
    return map;
  }, [rooms]);

  // Room-identity busy-night colouring (design-brief-2027 Part B, 2026-11-04):
  // when a room is selected its own busy nights take THAT room's identity
  // colour in the calendar, while house-wide busy nights stay slate for every
  // room — the two screenshots the owner compared can never look identical
  // again. Without a selected room the calendar keeps the single grey union
  // (sold-out / house-wide), which is already the "every room" truth.
  const busyDayModifiers = useMemo(() => {
    if (!rooms || rooms.length === 0) return {};
    const mods: Record<string, (date: Date) => boolean> = {};
    for (let i = 0; i < rooms.length; i += 1) {
      const room = rooms[i];
      mods[roomBusyModifierKey(i)] = (date: Date) => {
        const iso = toIsoDay(toDateAtMidnight(date));
        return (
          Boolean(roomBusyDays?.[room.id]?.has(iso)) && !houseBusyDays.has(iso)
        );
      };
    }
    return mods;
  }, [rooms, roomBusyDays, houseBusyDays]);

  const busyDayModifierClassNames = useMemo(() => {
    if (!rooms || rooms.length === 0) return {};
    const classes: Record<string, string> = {
      [HOUSE_WIDE_BUSY_MODIFIER_KEY]: HOUSE_WIDE_BUSY.cell,
    };
    for (let i = 0; i < rooms.length; i += 1) {
      classes[roomBusyModifierKey(i)] = getRoomIdentityColour(i).cell;
    }
    return classes;
  }, [rooms]);


  const houseWideModifiers = useMemo(() => ({
    [HOUSE_WIDE_BUSY_MODIFIER_KEY]: (date: Date) => {
      if (!rooms || rooms.length === 0) return false;
      const iso = toIsoDay(toDateAtMidnight(date));
      // A fully sold-out night (every room busy) renders the same slate union
      // as house-wide events — the guest cannot book anything either way.
      return (
        houseBusyDays.has(iso) &&
        (selectedRoomId ? Boolean(roomBusyDays?.[selectedRoomId]?.has(iso)) : false)
      );
    },
  }), [rooms, houseBusyDays, selectedRoomId, roomBusyDays]);

  // "Book this room" in the public Rooms section pre-selects the room and
  // scrolls here. The rooms list loads async, so a pick that arrives before
  // the list is held in a ref until the list can honour it.
  const pendingRoomSelectRef = useRef<string | null>(null);
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ roomId?: string }>).detail;
      const roomId = typeof detail?.roomId === "string" ? detail.roomId : "";
      if (!roomId) return;
      const list = rooms || [];
      if (list.some((room) => room.id === roomId)) {
        setSelectedRoomId(roomId);
      } else if (rooms === null) {
        pendingRoomSelectRef.current = roomId;
      }
    };
    window.addEventListener("zamzi:select-room", handler);
    return () => window.removeEventListener("zamzi:select-room", handler);
  }, [rooms]);

  useEffect(() => {
    if (availabilityOverride) {
      const parsedBusy = (availabilityOverride.busy || [])
        .map((item) => ({
          start: new Date(item.start),
          end: new Date(item.end),
        }))
        .filter((item) => !Number.isNaN(item.start.getTime()) && !Number.isNaN(item.end.getTime()) && item.start < item.end);

      setBusyIntervals(parsedBusy);
      setBlackoutDates(new Set((availabilityOverride.blackoutDates || []).filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))));
      setAvailabilityError(null);
      setAvailabilityLoading(false);
      return;
    }

    const controller = new AbortController();
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    fetchAvailability(profileOwnerId, controller.signal)
      .then((data) => {
        const parsedBusy = (data.busy || [])
          .map((item) => ({
            start: new Date(item.start),
            end: new Date(item.end),
          }))
          .filter((item) => !Number.isNaN(item.start.getTime()) && !Number.isNaN(item.end.getTime()) && item.start < item.end);

        setBusyIntervals(parsedBusy);
        setBlackoutDates(new Set((data.blackoutDates || []).filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))));
      })
      .catch((error: any) => {
        if (error?.name === "AbortError") return;
        setAvailabilityError(error?.message || "Failed to load availability");
      })
      .finally(() => {
        setAvailabilityLoading(false);
      });

    return () => controller.abort();
  }, [profileOwnerId, availabilityOverride]);

  // ------------------------- Scheduling helpers -------------------------

  const busyRanges = useMemo(() => toBusyRanges(availability), [availability]);
  const slotOptions = useMemo(() => buildSlotOptions(availability, busyRanges), [availability, busyRanges]);
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const selectedIsoDate = DateTime.fromJSDate(selectedDate, { zone: timezone }).toISODate();
    return slotOptions.filter((slot) => slot.start.toISODate() === selectedIsoDate);
  }, [slotOptions, selectedDate, timezone]);
  const availableDates = useMemo(() => {
    const uniqueIsoDays = Array.from(new Set(slotOptions.map((slot) => slot.start.toISODate()).filter(Boolean)));
    return uniqueIsoDays.map((isoDate) => DateTime.fromISO(isoDate!, { zone: timezone }).toJSDate());
  }, [slotOptions, timezone]);
  const selectedSlot = slotOptions.find((slot) => slot.id === selectedSlotId) ?? null;

  const preferredDateValue = scheduleForm.watch("preferredDate");
  const preferredTimeValue = scheduleForm.watch("preferredTime");

  const manualSelectionConflicts = useMemo(() => {
    if (!preferredDateValue || !preferredTimeValue || !availability) {
      return false;
    }
    const candidate = DateTime.fromISO(`${preferredDateValue}T${preferredTimeValue}`, { zone: timezone });
    if (!candidate.isValid) {
      return false;
    }
    return computeIsBusy(candidate, busyRanges);
  }, [availability, preferredDateValue, preferredTimeValue, timezone, busyRanges]);

  useEffect(() => {
    if (!autoSelectFirstAvailableSlot || selectedSlotId || slotOptions.length === 0) {
      return;
    }
    const firstOpenSlot = slotOptions.find((slot) => !slot.disabled);
    if (!firstOpenSlot) {
      return;
    }
    setSelectedDate(firstOpenSlot.start.startOf("day").toJSDate());
    setSelectedSlotId(firstOpenSlot.id);
    scheduleForm.setValue("preferredDate", firstOpenSlot.start.toISODate() || "", { shouldDirty: true });
    scheduleForm.setValue("preferredTime", firstOpenSlot.start.toFormat("HH:mm"), { shouldDirty: true });
  }, [autoSelectFirstAvailableSlot, scheduleForm, selectedSlotId, slotOptions]);

  useEffect(() => {
    if (!selectedSlotId) {
      return;
    }
    const slot = slotOptions.find((candidate) => candidate.id === selectedSlotId);
    if (!slot) {
      setSelectedSlotId(null);
      return;
    }
    const slotDate = slot.start.toISODate();
    const slotTime = slot.start.toFormat("HH:mm");
    if (preferredDateValue !== slotDate || preferredTimeValue !== slotTime) {
      setSelectedSlotId(null);
    }
  }, [preferredDateValue, preferredTimeValue, selectedSlotId, slotOptions]);

  useEffect(() => {
    if (!completedBooking || !successCardRef.current) {
      return;
    }
    successCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [completedBooking]);

  const completedBookingId = completedBooking?.kind === "request" ? completedBooking.bookingId : null;
  const completedBookingAccessToken = completedBooking?.kind === "request" ? completedBooking.accessToken : null;
  const completedBookingStatus = completedBooking?.kind === "request" ? completedBooking.status : null;

  useEffect(() => {
    if (!completedBookingId || completedBookingStatus === "confirmed" || !completedBookingAccessToken) {
      return;
    }

    let isCancelled = false;
    const syncStatus = async () => {
      try {
        const params = new URLSearchParams({
          profileId: profileOwnerId,
          bookingId: completedBookingId,
          accessToken: completedBookingAccessToken || "",
        });
        const response = await fetch(`/api/bookings/status?${params.toString()}`);
        const payload = (await response.json().catch(() => ({}))) as BookingStatusPayload;
        if (!response.ok || isCancelled) {
          return;
        }
        const isConfirmed = (payload.status || "").toLowerCase() === "confirmed";
        const paymentEnabled = Boolean(payload.payment?.enabled && payload.payment?.provider === "payfast");
        const paymentVerified = Boolean(payload.payment?.verified);
        setCompletedBooking((current) => {
          if (!current || current.kind !== "request" || current.bookingId !== completedBookingId) {
            return current;
          }
          return {
            ...current,
            status: isConfirmed ? "confirmed" : current.status,
            paymentEnabled,
            paymentVerified,
          };
        });
      } catch {
        // Non-blocking: keep showing pending state and retry on next interval.
      }
    };

    void syncStatus();
    const intervalId = window.setInterval(syncStatus, 15000);
    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [completedBookingId, completedBookingAccessToken, completedBookingStatus, profileOwnerId]);

  const redirectToEnquiry = (message: string) => {
    toast({
      title: "Switching to enquiry",
      description: message,
      variant: "destructive",
    });
    const hashTarget = typeof window !== "undefined"
      ? `${window.location.pathname}#lead-inquiry`
      : `#lead-inquiry`;
    router.push(hashTarget);
  };

  const handleSlotSelect = (slot: SlotOption) => {
    if (slot.disabled || completedBooking) {
      return;
    }
    setSelectedSlotId(slot.id);
    scheduleForm.setValue("preferredDate", slot.start.toISODate() || "", { shouldDirty: true });
    scheduleForm.setValue("preferredTime", slot.start.toFormat("HH:mm"), { shouldDirty: true });
  };

  const clearSlotSelection = () => {
    setSelectedSlotId(null);
    scheduleForm.setValue("preferredDate", "", { shouldDirty: true });
    scheduleForm.setValue("preferredTime", "", { shouldDirty: true });
  };

  const handleGuestAction = async (action: "confirm" | "cancel") => {
    if (completedBooking?.kind !== "instant" || !completedBooking.accessToken) {
      toast({
        title: "No booking to manage",
        description: "Please submit a booking request first.",
        variant: "destructive",
      });
      return;
    }

    setGuestActionLoading(action);
    setGuestActionMessage(null);
    try {
      const endpoint = action === "confirm" ? "confirmReservationWithToken" : "cancelReservationWithToken";
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_FUNCTIONS_URL || "https://us-central1-zamzi-connect.cloudfunctions.net"}/${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            profileId: profileOwnerId,
            bookingId: completedBooking.bookingId,
            accessToken: completedBooking.accessToken,
          }),
        },
      );

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.message || "Request failed");
      }

      if (action === "confirm") {
        setCompletedBooking({ ...completedBooking, state: "instant" });
        setGuestActionMessage("Booking confirmed. The business has been notified.");
        toast({ title: "Booking confirmed", description: "You're all set!" });
      } else {
        setCompletedBooking({ ...completedBooking, state: "cancelled", accessToken: null });
        setGuestActionMessage("Booking cancelled. We released the slot.");
        toast({ title: "Booking cancelled", description: "The slot has been released." });
      }
    } catch (error: any) {
      toast({
        title: action === "confirm" ? "Confirmation failed" : "Cancellation failed",
        description: error?.message || "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setGuestActionLoading(null);
    }
  };

  // ------------------------- Stay helpers -------------------------

  const today = useMemo(() => toDateAtMidnight(new Date()), []);

  const isDateBlocked = (date: Date) => {
    const day = toDateAtMidnight(date);
    if (day < today) return true;

    const iso = toIsoDay(day);
    if (blackoutDates.has(iso)) return true;

    // Multi-room properties: the calendar reflects ROOMS, never the property
    // document's own (legacy/whole-house) bookings. A day greys out when the
    // selected room is busy, or — no room picked — when every room is taken.
    if (rooms && rooms.length > 0) {
      if (selectedRoomId && roomBusyDays) {
        return Boolean(roomBusyDays[selectedRoomId]?.has(iso));
      }
      return soldOutDays.has(iso);
    }

    if (soldOutDays.has(iso)) return true;

    const dayEnd = addDays(day, 1);
    return busyIntervals.some((interval) => datesOverlap(day, dayEnd, interval.start, interval.end));
  };

  const selectedNights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    const from = toDateAtMidnight(range.from);
    const to = toDateAtMidnight(range.to);
    const ms = to.getTime() - from.getTime();
    return Math.max(0, Math.round(ms / 86400000));
  }, [range]);

  const selectionConflicts = useMemo(() => {
    if (!range?.from || !range?.to) return false;
    const start = toDateAtMidnight(range.from);
    const end = toDateAtMidnight(range.to);
    if (start >= end) return true;
    // Each busy day is a NIGHT [day, day+1); the checkout day itself is not a
    // night, so leaving on a taken day is fine.

    // Multi-room: the selected room's own diary, or — no room picked — the
    // nights when every room is taken (a stay needs somewhere to sleep).
    if (rooms && rooms.length > 0) {
      const busyDays =
        selectedRoomId && roomBusyDays
          ? Array.from(roomBusyDays[selectedRoomId] || [])
          : Array.from(soldOutDays);
      for (const iso of busyDays) {
        const nightStart = toDateAtMidnight(new Date(`${iso}T00:00:00`));
        if (nightStart >= start && nightStart < end) return true;
      }
      return false;
    }

    // Sold-out nights (every room busy) cannot be part of any stay.
    if (soldOutDays.size > 0) {
      for (const iso of soldOutDays) {
        const nightStart = toDateAtMidnight(new Date(`${iso}T00:00:00`));
        if (nightStart >= start && nightStart < end) return true;
      }
    }
    return busyIntervals.some((interval) => datesOverlap(start, end, interval.start, interval.end));
  }, [range, busyIntervals, soldOutDays, rooms, selectedRoomId, roomBusyDays]);

  const formatDay = (value?: Date) => {
    if (!value) return "";
    return value.toLocaleDateString("en-ZA", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ------------------------- Submissions -------------------------

  const launchSecureCheckout = async (bookingId: string, accessToken?: string | null, profileIdOverride?: string | null) => {
    if (!accessToken) {
      toast({
        title: "Secure payment unavailable",
        description: "Your booking session token is missing. Please contact the host to resend your confirmation link.",
        variant: "destructive",
      });
      return;
    }

    setPaymentCheckoutLoading(true);
    try {
      await launchPayfastCheckout({ profileOwnerId: profileIdOverride || profileOwnerId, bookingId, accessToken });
    } catch (error) {
      toast({
        title: "Secure payment unavailable",
        description: error instanceof Error ? error.message : "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setPaymentCheckoutLoading(false);
    }
  };

  const submitInstantBooking = async (values: ScheduleValues) => {
    if (!selectedSlot) {
      toast({ title: "Pick a slot", description: "Please choose a time before booking.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const slotStart = selectedSlot.start.toUTC();
      const slotEnd = selectedSlot.end.toUTC();

      const customerName = values.name.trim();
      const customerEmail = values.email?.trim() || undefined;

      const reserveResult = await submitReserveSlot({
        profileOwnerId,
        start: slotStart.toISO(),
        end: slotEnd.toISO(),
        customerName,
        customerEmail,
        metadata: {
          intent: "instant-book",
          note: values.message || null,
          submittedAt: new Date().toISOString(),
          timezone,
          serviceContext: serviceContext.map((item) => String(item || "").trim()).filter(Boolean),
        },
      });

      if (requiresSecurePayment) {
        // Bug 3 fix: accessToken from reserveSlot must be forwarded to checkout.
        const reservedAccessToken = typeof reserveResult.accessToken === "string" ? reserveResult.accessToken.trim() : "";
        if (!reservedAccessToken) {
          throw new Error("Could not obtain a secure booking token from the server. Please try again.");
        }
        toast({
          title: "Continue to secure payment",
          description: "Redirecting you to PayFast to complete this booking payment.",
        });
        await launchSecureCheckout(reserveResult.bookingId, reservedAccessToken);
        return;
      }

      const bookingStatus: "confirmed" | "pending" =
        reserveResult.status === "confirmed" ? "confirmed" : "pending";

      setCompletedBooking({
        kind: "instant",
        state: bookingStatus === "confirmed" ? "instant" : "approval",
        bookingId: reserveResult.bookingId,
        accessToken: typeof reserveResult.accessToken === "string" ? reserveResult.accessToken : null,
      });

      const slotLine = `Slot: ${slotStart.setZone(timezone).toFormat("ccc, dd LLL yyyy HH:mm")} - ${slotEnd.setZone(timezone).toFormat("HH:mm")}`;
      const statusHeader = bookingStatus === "confirmed"
        ? "Instant Booking (Confirmed)"
        : "Instant Booking (Awaiting Approval)";
      const statusDetail = bookingStatus === "confirmed"
        ? "Status: Confirmed automatically and added to your calendar."
        : "Status: Waiting for your approval.";
      const guestNoteLine = values.message ? `Guest Note: ${values.message}` : null;

      saveBookingLead({
        name: values.name,
        email: values.email || "",
        whatsappNumber: values.whatsappNumber,
        profileOwnerId,
        bookingId: reserveResult.bookingId,
        intent: bookingStatus === "confirmed" ? "instant-book" : "booking-request",
        message: [statusHeader, statusDetail, slotLine, guestNoteLine].filter(Boolean).join("\n"),
      }).catch((error) => {
        console.error("Failed to log lead for instant booking", error);
      });

      toast({
        title: bookingStatus === "confirmed" ? "Booking confirmed!" : "Instant book request sent!",
        description: bookingStatus === "confirmed"
          ? `${profileOwnerName} will see this in their calendar.`
          : `${profileOwnerName} has been notified to approve your slot.`,
      });
      scheduleForm.reset();
      setSelectedSlotId(null);
      setSelectedDate(undefined);
    } catch (error: any) {
      console.error("Instant booking failed", error);
      redirectToEnquiry(error?.message || "We couldn't book that slot automatically.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const logStayBookingRequestEvent = (payload: {
    bookingId?: string;
    bookingMode: string;
    checkInDate?: string;
    checkOutDate?: string;
    nights?: number;
    guests?: number;
    viewerWhatsapp: string | null;
    viewerEmail: string | null;
  }) => {
    const viewerId = auth.currentUser?.uid ?? null;
    void incrementBookingRequest(profileOwnerId, viewerId).catch(() => undefined);
    void fetch("/api/telemetry/lead-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        profileId: profileOwnerId,
        product: "stay",
        eventType: "booking_request",
        source: "stay-booking-form",
        viewerId,
        viewerWhatsapp: payload.viewerWhatsapp,
        viewerEmail: payload.viewerEmail,
        metadata: {
          bookingId: payload.bookingId ?? null,
          bookingMode: payload.bookingMode,
          checkInDate: payload.checkInDate ?? null,
          checkOutDate: payload.checkOutDate ?? null,
          nights: payload.nights ?? null,
          guests: payload.guests ?? null,
        },
      }),
    }).catch(() => undefined);
  };

  const submitBookingRequest = async (values: ScheduleValues) => {
    const tz = timezone;
    let slotStart: DateTime | null = null;
    let slotEnd: DateTime | null = null;

    if (selectedSlot) {
      slotStart = selectedSlot.start;
      slotEnd = selectedSlot.end;
    } else if (values.preferredDate && values.preferredTime) {
      slotStart = DateTime.fromISO(`${values.preferredDate}T${values.preferredTime}`, { zone: tz });
      slotEnd = slotStart.plus({ minutes: SLOT_DURATION_MINUTES });
    }

    if (!slotStart || !slotEnd || !slotStart.isValid || !slotEnd.isValid) {
      toast({
        title: "Pick a time",
        description: "Please choose a date and time for your booking request.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedSlot && computeIsBusy(slotStart, busyRanges)) {
      toast({
        title: "Slot unavailable",
        description: "That time is already booked. Please choose another slot.",
        variant: "destructive",
      });
      return;
    }

    const customerNote = values.message?.trim() || "";
    if (customerNote.length < 10) {
      toast({
        title: "Add a note",
        description: "Please tell the business owner a little about your booking (at least 10 characters).",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const reserveResult = await submitReserveSlot({
        profileOwnerId,
        start: slotStart.toUTC().toISO(),
        end: slotEnd.toUTC().toISO(),
        customerName: values.name.trim(),
        customerEmail: values.email?.trim() || undefined,
        note: customerNote,
        metadata: {
          intent: "booking-request",
          whatsappNumber: values.whatsappNumber?.trim() || null,
          submittedAt: new Date().toISOString(),
          timezone: tz,
          serviceContext: serviceContext.map((item) => String(item || "").trim()).filter(Boolean),
        },
      });

      const slotLabel = `${slotStart.setZone(tz).toFormat("ccc, dd LLL yyyy")} at ${slotStart.setZone(tz).toFormat("HH:mm")}`;
      const shouldAutoConfirm = qaAutoConfirmAfterSubmit;
      setCompletedBooking({
        kind: "request",
        bookingId: reserveResult.bookingId,
        slotLabel,
        accessToken: typeof reserveResult.accessToken === "string" ? reserveResult.accessToken : null,
        status: shouldAutoConfirm ? "confirmed" : "pending",
        paymentEnabled: shouldAutoConfirm,
        paymentVerified: false,
      });

      saveBookingLead({
        name: values.name,
        email: values.email,
        whatsappNumber: values.whatsappNumber,
        message: `Booking Request for ${slotLabel}\n\n${customerNote}`,
        profileOwnerId,
        bookingId: reserveResult.bookingId,
        intent: "booking-request",
        keepalive: true,
      }).catch((error) => {
        console.error("[booking-request] lead capture failed:", error);
      });

      toast({
        title: shouldAutoConfirm ? "Booking request accepted!" : "Booking request sent!",
        description: shouldAutoConfirm
          ? `${profileOwnerName} accepted your slot.`
          : `${profileOwnerName} has 24 hours to confirm your slot.`,
      });

      scheduleForm.reset();
      setSelectedSlotId(null);
      setSelectedDate(undefined);
    } catch (error: any) {
      console.error("Failed to send booking request:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to send your request. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitScheduling = (values: ScheduleValues) => {
    if (mode === "instant") {
      return submitInstantBooking(values);
    }
    return submitBookingRequest(values);
  };

  // Approval-mode guests never pay up front (the server refuses payment on a
  // pending manual booking), so the only pay-immediately path is instant mode;
  // everyone else waits for the host's confirmation email.
  const onStaySubmit = async (values: StayBookingValues) => {
    const from = range?.from ? toDateAtMidnight(range.from) : null;
    const to = range?.to ? toDateAtMidnight(range.to) : null;

    if (!from || !to || from >= to) {
      toast({
        title: "Select dates",
        description: "Please choose both check-in and check-out dates.",
        variant: "destructive",
      });
      return;
    }

    if (selectionConflicts) {
      toast({
        title: "Dates unavailable",
        description: "Your selected dates overlap with blocked or already-booked dates. Please choose another range.",
        variant: "destructive",
      });
      return;
    }

    if (selectedNights < minimumNights) {
      toast({
        title: "Minimum stay required",
        description: `This stay requires at least ${minimumNights} night${minimumNights === 1 ? "" : "s"}.`,
        variant: "destructive",
      });
      return;
    }

    if (typeof maxGuests === "number" && maxGuests > 0 && guests > maxGuests) {
      toast({
        title: "Guest limit exceeded",
        description: `This stay allows up to ${maxGuests} guest${maxGuests === 1 ? "" : "s"}.`,
        variant: "destructive",
      });
      return;
    }

    // Multi-room property: a room must be chosen, and it must be free for
    // these dates. The authoritative conflict check still runs server-side at
    // reserve time — this guard just catches a stale selection early.
    if (rooms && rooms.length > 0 && !selectedRoomId) {
      toast({
        title: "Choose a room",
        description: "Pick which room you'd like for these dates.",
        variant: "destructive",
      });
      return;
    }
    if (rooms && rooms.length > 0 && selectedRoomId && !selectedRoomAvailable) {
      toast({
        title: "Dates unavailable",
        description: `${selectedRoom?.roomName || "That room"} is already booked for these dates. Please choose another room or change your dates.`,
        variant: "destructive",
      });
      return;
    }

    const note = String(values.note || "").trim();
    const messageLines = [
      `Stay booking request for ${profileOwnerName}`,
      selectedRoom ? `Room: ${selectedRoom.roomName}` : null,
      `Check-in: ${toIsoDay(from)}`,
      `Check-out: ${toIsoDay(to)}`,
      `Nights: ${selectedNights}`,
      `Guests: ${guests}`,
      note ? `Guest note: ${note}` : null,
    ].filter(Boolean);

    setIsSubmitting(true);
    try {
      // Reserve the full stay range using checkout-day midnight as an exclusive end.
      const reservationStart = new Date(from);
      const reservationEnd = new Date(to);

      const reserveResult = await submitReserveSlot({
        profileOwnerId: bookingProfileId,
        start: reservationStart.toISOString(),
        end: reservationEnd.toISOString(),
        customerName: values.name.trim(),
        customerEmail: values.email?.trim() || undefined,
        metadata: {
          intent: bookingMode === "instant" ? "instant-book" : "stay-booking-request",
          bookingMode,
          checkInDate: toIsoDay(from),
          checkOutDate: toIsoDay(to),
          nights: selectedNights,
          guests,
          guestWhatsapp: values.whatsappNumber?.trim() || null,
          selectedRoomId: selectedRoomId || null,
          selectedRoomName: selectedRoom?.roomName || null,
          submittedAt: new Date().toISOString(),
        },
        note: note || null,
      });

      const bookingId = reserveResult.bookingId || undefined;
      const accessToken = typeof reserveResult.accessToken === "string" ? reserveResult.accessToken : undefined;
      const status = reserveResult.status === "confirmed" ? "confirmed" : "pending";

      // Keep lead analytics/inbox continuity while booking docs become source of truth.
      // bookingProfileId: a room booking's doc lives under the ROOM's profile —
      // the accept handoff must confirm it there, not on the host's.
      saveBookingLead({
        name: values.name,
        email: values.email || "",
        whatsappNumber: values.whatsappNumber || "",
        profileOwnerId,
        bookingId,
        bookingProfileId: bookingProfileId,
        intent: bookingMode === "instant" ? "instant-book" : "booking-request",
        message: messageLines.join("\n"),
        keepalive: true,
      }).catch((error) => {
        console.error("[stay-booking-request] lead capture failed:", error);
      });

      logStayBookingRequestEvent({
        bookingId,
        bookingMode,
        checkInDate: toIsoDay(from),
        checkOutDate: toIsoDay(to),
        nights: selectedNights,
        guests,
        viewerWhatsapp: values.whatsappNumber?.trim() || null,
        viewerEmail: values.email?.trim() || null,
      });

      if (requiresSecurePayment && resolvedMode === "instant") {
        if (!bookingId) {
          throw new Error("Could not initialize secure payment for this booking.");
        }
        toast({
          title: "Continue to secure payment",
          description: "Redirecting you to PayFast to complete your stay booking.",
        });
        await launchSecureCheckout(bookingId, accessToken, bookingProfileId);
        return;
      }

      setStayCompleted({ bookingId, accessToken, status });
      stayForm.reset();
      toast({
        title: status === "confirmed" ? "Stay booked" : "Booking request sent",
        description:
          status === "confirmed"
            ? `Your stay was confirmed immediately with ${profileOwnerName}.`
            : `Your request was sent to ${profileOwnerName}. Once they confirm, we'll email you a secure payment link.`,
        style: { backgroundColor: "#001489", color: "#FFFFFF" },
      });
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : "Failed to send your booking request. Please try again.";
      console.error("Failed to submit stay booking request", error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isStay) {
    if (stayCompleted) {
      return (
        <div className="text-center p-8 border rounded-lg bg-secondary/50 mt-8">
          <h3 className="text-lg font-semibold text-primary">
            {stayCompleted.status === "confirmed" ? "Stay booked" : "Booking request sent"}
          </h3>
          <p className="text-muted-foreground mt-2">
            {stayCompleted.status === "confirmed"
              ? `Your stay with ${profileOwnerName} is confirmed.`
              : `We sent your check-in and check-out request to ${profileOwnerName}. They can now confirm availability and next steps.`}
          </p>
          <button
            type="button"
            onClick={() => {
              setStayCompleted(null);
              setRange(undefined);
              stayForm.reset();
            }}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-[#001489] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#001489]/90"
          >
            Start another booking
          </button>
        </div>
      );
    }

    const stayMode = bookingMode === "instant" ? "instant" : "request";
    const isStayInstant = stayMode === "instant";
    const balancePercent = 100 - displayDepositPercent;

    return (
      <Card className="shadow-lg mt-8" id="stay-booking-form">
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: "#001489" }}>
            {ctaText || (stayMode === "instant" ? "Book Your Stay Instantly" : "Book Your Stay")}
          </CardTitle>
          <CardDescription>
            {/*
              Both branches promise that paying secures the stay, so both have to
              put the host's approval first on a request-mode listing. The instant
              branch is unchanged: there the guest really does secure the stay by
              paying.
            */}
            {requiresSecurePayment
              ? isStayInstant
                ? `Pick your dates, reserve your stay, then complete secure payment to lock it in with Zamzi ${displayDepositPercent}/${balancePercent} Protection.`
                : `Pick your dates and send your request to the host. Once they approve, complete secure payment with Zamzi ${displayDepositPercent}/${balancePercent} Protection.`
              : isStayInstant
                ? "Pick your dates to confirm your stay instantly when available."
                : "Pick check-in and check-out dates to send your stay request directly to the host."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-[#0014891f] bg-[#0014890d] p-3 text-sm text-[#001489]">
            <p className="font-semibold">Zamzi {displayDepositPercent}/{balancePercent} Protection</p>
            {isStayInstant ? (
              <p className="mt-1">Secure your booking with a {displayDepositPercent}% deposit via PayFast now, then pay the remaining {balancePercent}% after your safe arrival is confirmed.</p>
            ) : (
              <p className="mt-1">Send your request first. Once the host approves, pay a {displayDepositPercent}% deposit via PayFast to secure your booking, then pay the remaining {balancePercent}% after your safe arrival is confirmed.</p>
            )}
          </div>

          {requiresSecurePayment && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900">Secure payment required</p>
              <p className="mt-1 text-sm text-emerald-800">
                {isStayInstant ? (
                  <>This host accepts secure booking payments through PayFast. A {displayDepositPercent}% deposit is required to secure your booking. The exact deposit amount is calculated from your selected dates at checkout.</>
                ) : (
                  <>This host accepts secure booking payments through PayFast. Once the host approves your request, a {displayDepositPercent}% deposit secures your booking. The exact deposit amount is calculated from your selected dates at checkout.</>
                )}
              </p>
              {/*
                The anti-fraud warning is deliberately NOT branched. It is true in
                both modes and it is the part that keeps a guest from paying the
                host directly, which is the outcome the whole payment model exists
                to prevent.
              */}
              <p className="mt-3 text-xs font-semibold text-emerald-900 bg-white rounded px-2 py-1.5 border border-emerald-300">
                ⚠️ Important: Pay ONLY through PayFast. Do not send cash or direct payments to the host. This is the only way to ensure your Zamzi booking protection.
              </p>
            </div>
          )}

          {availabilityLoading && (
            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              Checking live stay availability...
            </div>
          )}

          {availabilityError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {availabilityError}
            </div>
          )}

          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm font-semibold text-foreground mb-2">Choose dates</p>
            <div className="w-full overflow-hidden">
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange as any}
                numberOfMonths={2}
                disabled={isDateBlocked}
                modifiers={{ ...busyDayModifiers, ...houseWideModifiers }}
                modifiersClassNames={busyDayModifierClassNames}
                className="w-full rounded-md border bg-white"
              />
            </div>
            {(rooms?.length ?? 0) > 0 && (
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                {selectedRoomId && rooms ? (
                  <>
                    <p className="flex flex-wrap items-center gap-2">
                      <span
                        aria-hidden
                        className={`inline-block h-2.5 w-2.5 rounded-sm ${(() => {
                          const idx = rooms.findIndex((room) => room.id === selectedRoomId);
                          return idx >= 0 ? getRoomIdentityColour(idx).dot : "bg-slate-400";
                        })()}`}
                      />
                      <span>
                        {rooms.find((room) => room.id === selectedRoomId)?.roomName || "This room"} busy nights
                      </span>
                      <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-500" />
                      <span>House-wide busy</span>
                    </p>
                    <p>
                      {`Nights in ${rooms.find((room) => room.id === selectedRoomId)?.roomName || "this room"}'s colour are nights it's already booked — house-wide busy nights darken every room the same.`}
                    </p>
                  </>
                ) : (
                  <p className="flex flex-wrap items-center gap-2">
                    <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-500" />
                    <span>
                      {soldOutDays.size > 0
                        ? "Nights when every room is already booked. Pick dates, then choose your room."
                        : "Nights when every room is already booked."}
                    </span>
                  </p>
                )}
              </div>
            )}

            <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
              <p>
                Check-in: <span className="font-semibold text-slate-800">{formatDay(range?.from) || "Not selected"}</span>
              </p>
              <p>
                Check-out: <span className="font-semibold text-slate-800">{formatDay(range?.to) || "Not selected"}</span>
              </p>
              <p>
                Nights: <span className="font-semibold text-slate-800">{selectedNights}</span>
              </p>
              <p>
                Minimum stay: <span className="font-semibold text-slate-800">{minimumNights}</span>
              </p>
            </div>

            {selectionConflicts && (
              <p className="mt-2 text-xs text-destructive">
                Selected dates overlap with blocked or already-booked dates.
              </p>
            )}
          </div>

          {(() => {
            // When a room is chosen, its own rate and cleaning fee are what
            // the guest will actually be charged — show those, not the
            // property defaults.
            const chosenRoom = rooms?.find((room) => room.id === selectedRoomId) || null;
            const displayRate = chosenRoom?.pricePerNight ?? nightlyRateZar ?? null;
            const displayCleaning = chosenRoom?.cleaningFee ?? cleaningFeeZar;
            if (!displayRate || displayRate <= 0 || !range?.from || !range?.to || selectedNights <= 0) return null;

            // G6: the rate that governs is the chosen room's own when selected
            // (its seasons travel with it); otherwise the property's rate with
            // the property's seasons. Same average math, same pricing as the
            // checkout snapshot locks — parity by construction.
            const seasonalRows = coerceSeasonalRates(
              chosenRoom ? chosenRoom.seasonalRates : seasonalRates,
            );
            let seasonalQuote: {
              averageNightlyRate: number;
              cheapestNight: number;
              dearestNight: number;
            } | null = null;
            if (seasonalRows.length > 0) {
              const seasonalWindow = averageSeasonalNightlyRate({
                rates: seasonalRows,
                fallbackRate: displayRate,
                startDate: range.from,
                endDate: range.to,
              });
              if (windowTouchesSeason(seasonalWindow, displayRate)) {
                seasonalQuote = {
                  averageNightlyRate: seasonalWindow.averageNightlyRate,
                  cheapestNight: seasonalWindow.cheapestNight,
                  dearestNight: seasonalWindow.dearestNight,
                };
              }
            }

            return (
              <StayPriceBreakdownCard
                nightlyRate={seasonalQuote ? seasonalQuote.averageNightlyRate : displayRate}
                originalNightlyRate={chosenRoom || seasonalQuote ? undefined : originalNightlyRateZar}
                nights={selectedNights}
                depositPercent={depositPercent}
                cleaningFeeZar={displayCleaning}
                showDepositSplit={requiresSecurePayment}
                compact={true}
                seasonal={seasonalQuote}
              />
            );
          })()}

          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm font-semibold text-foreground mb-2">Guests</p>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setGuests((current) => Math.max(1, current - 1))}
              >
                -
              </Button>
              <span className="min-w-12 text-center text-sm font-semibold text-slate-800">{guests}</span>
              <Button
                type="button"
                variant="outline"
                onClick={() => setGuests((current) => {
                  if (typeof maxGuests === "number" && maxGuests > 0) {
                    return Math.min(maxGuests, current + 1);
                  }
                  return current + 1;
                })}
              >
                +
              </Button>
              {typeof maxGuests === "number" && maxGuests > 0 && (
                <span className="text-xs text-slate-500">Max {maxGuests}</span>
              )}
            </div>
          </div>

          <Form {...stayForm}>
            <form onSubmit={stayForm.handleSubmit((values) => onStaySubmit(values))} className="space-y-4">
              {rooms && rooms.length > 0 && (
                <div className="space-y-2 rounded-md border p-3">
                  <p className="text-sm font-medium">
                    {range?.from && range?.to ? "Choose your room" : "Pick your dates to see which rooms are open"}
                  </p>
                  {range?.from && range?.to ? (
                    <>
                      <div className="space-y-2">
                        {rooms.map((room, roomIndex) => {
                          const free = room.availableForWindow !== false;
                          const tooSmall = isRoomTooSmall(room.sleeps, guests);
                          const bookable = free && !tooSmall;
                          const isSelected = selectedRoomId === room.id;
                          const thumbUrl = room.photoUrls?.[0];
                          const identity = getRoomIdentityColour(roomIndex);
                          return (
                            <button
                              key={room.id}
                              type="button"
                              disabled={!bookable}
                              onClick={() => setSelectedRoomId(isSelected ? null : room.id)}
                              className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                                isSelected
                                  ? `bg-primary/5 font-medium ring-2 ${identity.ring}`
                                  : bookable
                                    ? "hover:bg-secondary/60"
                                    : "cursor-not-allowed opacity-50"
                              }`}
                            >
                              {thumbUrl ? (
                                <span className="relative h-12 w-16 shrink-0 overflow-hidden rounded border border-slate-200">
                                  <Image src={thumbUrl} alt={room.roomName} fill sizes="64px" className="object-cover" />
                                </span>
                              ) : null}
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-1.5">
                                  <span
                                    aria-hidden
                                    title={`this room's calendar colour`}
                                    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${identity.dot}`}
                                  />
                                  <span className="block truncate font-medium">{room.roomName}</span>
                                </span>
                                <span className="text-muted-foreground">
                                  {room.sleeps ? `sleeps ${room.sleeps}` : null}
                                  {free && tooSmall ? ` — too small for ${guests}` : ""}
                                </span>
                              </span>
                              <span className="shrink-0 text-right">
                                {free
                                  ? room.pricePerNight !== null
                                    ? `R${Math.round(room.pricePerNight).toLocaleString("en-ZA")}/night`
                                    : "Available"
                                  : "Booked for these dates"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {rooms.some((room) => room.availableForWindow === false) && rooms.every((room) => room.availableForWindow === false) && (
                        <p className="text-xs text-destructive">
                          No rooms are open for these dates. Try shifting your stay by a night or two.
                        </p>
                      )}
                      {rooms.every((room) => isRoomTooSmall(room.sleeps, guests)) && (
                        <p className="text-xs text-destructive">
                          Your group of {guests} is bigger than every room here — send the host an inquiry about two rooms.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      <p>
                        {rooms.length} room{rooms.length === 1 ? "" : "s"} available at this property — each with its own calendar.
                      </p>
                      {selectedRoomId && (
                        <p className="mt-1 font-medium text-foreground">
                          {rooms.find((room) => room.id === selectedRoomId)?.roomName || "Your room"} is waiting — pick your dates to continue.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <FormField
                control={stayForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={stayForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Required so the host can confirm and send payment steps.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={stayForm.control}
                name="whatsappNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WhatsApp Number (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 0721234567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={stayForm.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes for host (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Arrival time, special requests, or anything the host should know..."
                        className="min-h-[90px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {requiresSecurePayment && stayMode === "request" && (
                <div className="rounded-lg border bg-muted/30 p-3">
                  <p className="text-sm text-muted-foreground">
                    Nothing is charged yet. Once the host confirms your dates, we'll email you a secure payment link.
                  </p>
                </div>
              )}

              <Button type="submit" className="w-full" style={{ backgroundColor: "#001489" }} disabled={isSubmitting || paymentCheckoutLoading}>
                {isSubmitting || paymentCheckoutLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {paymentCheckoutLoading ? "Redirecting to PayFast..." : "Securing your booking..."}
                  </>
                ) : requiresSecurePayment && stayMode === "request"
                  ? "Request to book"
                  : requiresSecurePayment
                    ? "Continue to secure payment"
                    : stayMode === "instant"
                      ? "Confirm stay instantly"
                      : "Send Stay Booking Request"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    );
  }

  // ------------------------- Scheduling (non-stay) view -------------------------

  if (completedBooking) {
    if (completedBooking.kind === "request") {
      return (
        <div ref={successCardRef} className="text-center p-8 border rounded-lg bg-secondary/50">
          <h3 className="text-lg font-semibold text-primary">Booking request sent</h3>
          <p className="text-muted-foreground mt-2">{profileOwnerName} has received your request.</p>
          <p className="text-sm font-medium text-foreground mt-3">
            Requested slot: <span className="font-bold">{completedBooking.slotLabel}</span>
          </p>
          {completedBooking.status === "confirmed" ? (
            <>
              <p className="text-sm font-medium text-foreground mt-1">Status: Accepted by the business owner.</p>
              {completedBooking.paymentEnabled && !completedBooking.paymentVerified ? (
                <div className="mt-4 space-y-2">
                  <Button type="button" onClick={() => launchSecureCheckout(completedBooking.bookingId, completedBooking.accessToken)} disabled={paymentCheckoutLoading} className="w-full sm:w-auto" style={{ backgroundColor: "#001489" }}>
                    {paymentCheckoutLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to PayFast...
                      </>
                    ) : (
                      "Pay securely with PayFast"
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">Payment unlocks final secure booking confirmation.</p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-2">No additional payment action is required right now.</p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground mt-1">Status: Awaiting confirmation from the business owner.</p>
              <p className="text-xs text-muted-foreground mt-2">
                They have 24 hours to confirm or suggest an alternative. Payment is only shown after acceptance.
              </p>
            </>
          )}
        </div>
      );
    }

    if (completedBooking.state === "cancelled") {
      return (
        <div ref={successCardRef} className="text-center p-8 border rounded-lg bg-secondary/50">
          <h3 className="text-lg font-semibold text-primary">Booking cancelled</h3>
          <p className="text-muted-foreground mt-2">
            The hold was released successfully. Pick another slot anytime.
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button type="button" variant="outline" onClick={() => setCompletedBooking(null)}>
              Pick another time
            </Button>
          </div>
          {guestActionMessage && (
            <p className="text-xs text-muted-foreground mt-3">{guestActionMessage}</p>
          )}
        </div>
      );
    }

    const title = completedBooking.state === "instant" ? "You're booked!" : "Awaiting approval";
    const description = completedBooking.state === "instant"
      ? `${profileOwnerName} has your confirmed booking. They'll reach out if anything changes.`
      : `${profileOwnerName} received your Instant Book request and will approve it shortly.`;
    const statusLine = completedBooking.state === "instant"
      ? "Status: Confirmed and added to the business calendar."
      : "Status: Reserved temporarily while the business reviews your request.";
    const showManageActions = Boolean(completedBooking.accessToken);
    const canSelfConfirm = completedBooking.state === "approval";

    return (
      <div ref={successCardRef} className="text-center p-8 border rounded-lg bg-secondary/50">
        <h3 className="text-lg font-semibold text-primary">{title}</h3>
        <p className="text-muted-foreground mt-2">{description}</p>
        <p className="text-sm font-medium text-foreground mt-3">{statusLine}</p>
        {showManageActions && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-muted-foreground">Need to make a change? Manage your booking below.</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              {canSelfConfirm && (
                <Button
                  variant="default"
                  disabled={guestActionLoading === "confirm"}
                  onClick={() => handleGuestAction("confirm")}
                  className="flex-1"
                >
                  {guestActionLoading === "confirm" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Confirming…
                    </>
                  ) : (
                    "Confirm booking"
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                disabled={guestActionLoading === "cancel"}
                onClick={() => handleGuestAction("cancel")}
                className="flex-1"
              >
                {guestActionLoading === "cancel" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cancelling…
                  </>
                ) : (
                  "Cancel booking"
                )}
              </Button>
            </div>
            {guestActionMessage && (
              <p className="text-xs text-muted-foreground">{guestActionMessage}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  const primaryLabel = requiresSecurePayment
    ? "Continue to secure payment"
    : mode === "instant"
      ? "Confirm this booking"
      : ctaText || "Send Booking Request";

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl font-bold" style={{ color: "#001489" }}>
          {mode === "instant"
            ? (ctaText || "Book instantly")
            : (ctaText || "Send a Booking Request")}
        </CardTitle>
        <CardDescription>
          {requiresSecurePayment
            ? "Instant Booking confirms immediately. Zamzi 10/90 Protection applies: secure with 10% deposit via PayFast now, then pay the remaining 90% after your safe arrival is confirmed."
            : mode === "instant"
              ? "Pick a live slot to confirm your booking immediately when it is free. You can also send a request instead."
              : "Choose a preferred time and send a booking request for the business owner to confirm manually."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {ownerSupportsInstant && (
            <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/30 p-1">
              <Button
                type="button"
                variant={mode === "instant" ? "default" : "ghost"}
                onClick={() => setMode("instant")}
                className="text-sm"
                style={mode === "instant" ? { backgroundColor: "#001489" } : undefined}
              >
                Book instantly
              </Button>
              <Button
                type="button"
                variant={mode === "request" ? "default" : "ghost"}
                onClick={() => setMode("request")}
                className="text-sm"
                style={mode === "request" ? { backgroundColor: "#001489" } : undefined}
              >
                Send a request instead
              </Button>
            </div>
          )}

          {requiresSecurePayment && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900">Secure payment required</p>
              <p className="mt-1 text-sm text-emerald-800">
                This booking is protected by Zamzi 10/90 Protection — PayFast payments only. A 10% deposit secures your stay and the remaining 90% is paid after your safe arrival is confirmed.
              </p>
              <p className="mt-2 text-xs text-emerald-900">
                ⚠️ Safety notice: Pay ONLY through PayFast. Never send cash or direct payments to the host outside of PayFast. This is the ONLY way to ensure your booking protection.
              </p>
            </div>
          )}

          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm font-semibold text-foreground">
              {mode === "instant"
                ? (requiresSecurePayment ? "Booking status: Pay to complete" : "Booking status: Instant confirmation")
                : "Booking status: Manual confirmation"}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {mode === "instant"
                ? (requiresSecurePayment
                  ? "Your slot is reserved first, then you are redirected to PayFast. Only verified payment callbacks are accepted."
                  : "If the slot is available, your booking is confirmed right away and appears on the business calendar.")
                : "Your chosen time is sent as a request. The business owner reviews it and confirms with you — no payment upfront."}
            </p>
          </div>

          {mode === "instant" && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary font-semibold flex items-start gap-2">
              <svg className="shrink-0 mt-0.5" width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 14h-2v-2h2v2zm0-4h-2V7h2v5z" fill="#001489" /></svg>
              <span>Bookings must be made at least <span className="font-bold">2 hours</span> in advance. This shield protects both you and the business owner from last-minute bookings.</span>
            </div>
          )}

          {mode === "request" && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary font-semibold flex items-start gap-2">
              <svg className="shrink-0 mt-0.5" width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 14h-2v-2h2v2zm0-4h-2V7h2v5z" fill="#001489" /></svg>
              <span>Bookings must be made at least <span className="font-bold">2 hours</span> in advance. This shield protects both you and the business owner from last-minute bookings.</span>
            </div>
          )}

          {availabilityLoading && (
            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              Checking live availability…
            </div>
          )}
          {availabilityError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {availabilityError}
            </div>
          )}

          {/* Bug 4 fix: warn early if payment is required but the host has no nightly rate configured,
              so the guest is never left with a reserved-but-unpayable slot. */}
          {requiresSecurePayment && (paymentConfig?.amountZar === null || paymentConfig?.amountZar === 0) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold">Pricing not yet configured</p>
              <p className="mt-1">The host has not set a nightly rate yet. Secure booking payment is unavailable until they do. Please contact the host directly.</p>
            </div>
          )}

          {!availabilityLoading && !availabilityError && slotOptions.length === 0 && (
            <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground space-y-3">
              <p>No live slots are available right now.</p>
              <p>You can still send a booking request or use the enquiry form so the business can follow up directly.</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                {mode === "request" ? (
                  <Button type="button" variant="outline" onClick={() => redirectToEnquiry("No live slots are available, so we moved you to the enquiry form.")}>
                    Use enquiry instead
                  </Button>
                ) : (
                  <>
                    <Button type="button" variant="outline" onClick={() => setMode("request")}>
                      Send a booking request
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => redirectToEnquiry("No live slots are available, so we moved you to the enquiry form.")}>
                      Use enquiry instead
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {slotOptions.length > 0 && (
            <div className="rounded-lg border bg-muted/30 p-4 w-full">
              <p className="text-sm font-semibold text-foreground mb-2">Pick a date to see available times</p>
              <div className="w-full overflow-hidden">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => !availableDates.some((d) => d.toDateString() === date.toDateString())}
                  className="w-full rounded-md border bg-white"
                />
              </div>
              {selectedDate && slotsForSelectedDate.length > 0 && (
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold">Available times for {DateTime.fromJSDate(selectedDate).toFormat('ccc, dd MMM yyyy')}</span>
                    {selectedSlotId && (
                      <Button type="button" variant="ghost" size="sm" onClick={clearSlotSelection}>
                        Clear
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 w-full">
                    {slotsForSelectedDate.map((slot) => (
                      <Button
                        key={slot.id}
                        type="button"
                        variant={slot.id === selectedSlotId ? "default" : "outline"}
                        size="sm"
                        disabled={slot.disabled || isSubmitting || paymentCheckoutLoading}
                        onClick={() => handleSlotSelect(slot)}
                        className={cn(
                          "w-full py-2 text-sm font-semibold justify-center",
                          slot.disabled && "opacity-50 line-through",
                          slot.id === selectedSlotId && "border-[#001489]"
                        )}
                      >
                        {slot.start.toFormat("HH:mm")} – {slot.end.toFormat("HH:mm")}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {selectedDate && slotsForSelectedDate.length === 0 && (
                <div className="mt-4 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                  No available times for this day. Try another date, or send a booking request below.
                </div>
              )}
            </div>
          )}

          {(selectedSlot && mode === "instant" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900">Selected slot</p>
              <p className="mt-1 text-sm text-emerald-800">
                {selectedSlot.start.toFormat("cccc, dd LLL yyyy")} at {selectedSlot.start.toFormat("HH:mm")} - {selectedSlot.end.toFormat("HH:mm")}
              </p>
              <p className="mt-2 text-xs text-emerald-700">
                Next step: submit to confirm this booking immediately if the slot is still free.
              </p>
            </div>
          ))}

          {(preferredDateValue || preferredTimeValue) && mode === "request" && selectedSlot && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
              <p className="text-sm font-semibold text-sky-900">Requested time</p>
              <p className="mt-1 text-sm text-sky-800">
                {preferredDateValue || "Date to be confirmed"}
                {preferredTimeValue ? ` at ${preferredTimeValue}` : ""}
              </p>
              <p className="mt-2 text-xs text-sky-700">
                Next step: send your request and wait for the business owner to confirm or suggest a better slot.
              </p>
            </div>
          )}
        </div>

        <Form {...scheduleForm}>
          <form onSubmit={scheduleForm.handleSubmit(submitScheduling)} className="mt-6 space-y-4">
            <FormField
              control={scheduleForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={scheduleForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} />
                  </FormControl>
                  <FormDescription>Required so we can send acceptance and payment updates.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={scheduleForm.control}
              name="whatsappNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., 0721234567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {mode === "request" && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={scheduleForm.control}
                  name="preferredDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        Selecting a slot above will fill this automatically.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={scheduleForm.control}
                  name="preferredTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      {manualSelectionConflicts && (
                        <p className="text-sm text-destructive">That time is already booked. Please choose another slot.</p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
            <FormField
              control={scheduleForm.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{mode === "request" ? "Booking Details" : `Notes for ${profileOwnerName}`}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={mode === "request"
                        ? `Hi ${profileOwnerName}, I'd like to book...`
                        : `Hi ${profileOwnerName}, I'm hoping to cover...`}
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {mode === "request" ? "Required (at least 10 characters) so the owner knows what you need." : "Optional — add anything the owner should know."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              style={{ backgroundColor: "#001489" }}
              disabled={isSubmitting || paymentCheckoutLoading || (mode === "request" ? manualSelectionConflicts : false)}
            >
              {isSubmitting || paymentCheckoutLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {paymentCheckoutLoading ? 'Redirecting to PayFast...' : 'Securing your booking...'}
                </>
              ) : (
                primaryLabel
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              {mode === "instant"
                ? "Changed your mind? Switch to \u201CSend a request instead\u201D above — no payment needed until the owner confirms."
                : "Manual requests stay flexible, so the business owner can confirm your time or offer the closest available option."}
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}